import {AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {Chart, ChartOptions, TooltipItem, registerables} from 'chart.js';
import {DebriefChart, DebriefSeries} from './sebg-debriefing.models';

Chart.register(...registerables);
const colors = ['#1d4ed8', '#be123c', '#047857', '#7e22ce', '#b45309', '#0e7490', '#c026d3', '#4d7c0f', '#4338ca', '#92400e'];

@Component({
  selector: 'app-debrief-chart', standalone: true,
  template: '<div class="chart-frame"><canvas #canvas role="img" [attr.aria-label]="description">Chart values are available in the details table below.</canvas></div>',
  styles: ['.chart-frame {position:relative;height:clamp(320px,52vh,650px);width:100%}']
})
export class DebriefChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() model!: DebriefChart;
  @Input() description = '';
  @Input() teamIds: string[] = [];
  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;
  private chart?: Chart;
  constructor(private zone: NgZone) {}
  ngAfterViewInit(): void { this.render(); }
  ngOnChanges(): void { if (this.canvas) this.render(); }
  ngOnDestroy(): void { this.chart?.destroy(); }

  // A polygon with a team-specific number of sides gives every team its own visible marker.
  private marker(index: number, radius = 10, color = colors[index % colors.length], historical = false): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = Math.round(radius * 2 + 2);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(canvas.width / 22, canvas.height / 22);
    const sides = index + 3;
    ctx.fillStyle = color;
    ctx.strokeStyle = historical ? color : '#111827';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < sides; i++) {
      const angle = i * 2 * Math.PI / sides - Math.PI / 2;
      const radius = index < 9 ? 9 : (i % 2 === 0 ? 10 : 4);
      const x = 11 + radius * Math.cos(angle), y = 11 + radius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill(); ctx.stroke();
    if (index >= 7) {
      ctx.fillStyle = '#ffffff'; ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center';
      ctx.textBaseline = 'middle'; ctx.fillText(String(index + 1), 11, 11);
    }
    return canvas;
  }

  private render(): void {
    this.chart?.destroy();
    if (!this.canvas || !this.model)
      return;
    const model = this.model;
    this.zone.runOutsideAngular(() => this.chart = new Chart(this.canvas!.nativeElement, {
      type: model.type,
      data: {
        labels: model.labels,
        datasets: model.series.map((series, index) => this.dataset(model, series, index))
      },
      options: this.options(model)
    }));
  }
  private dataset(model: DebriefChart, series: DebriefSeries, i: number) {
    const teamIndex = series.teamId ? Math.max(0, this.teamIds.indexOf(series.teamId)) : (series.styleIndex ?? i);
    const color = series.color ?? (series.benchmark ? '#111827' : colors[teamIndex % colors.length]);
    const markers = series.points?.map(p => this.marker(teamIndex, p?.radius ?? 0, p?.historical ? '#9ca3af' : color, p?.historical));
    const dataset = {
      type: series.type ?? model.type,
      label: series.label,
      data: series.points ? series.points.map(point => point ?? {x: NaN, y: NaN}) : series.values,
      borderColor: series.points ? '#e5e7eb' : color,
      backgroundColor: color,
      pointBackgroundColor: color,
      pointBorderColor: color,
      showLine: !!series.points || (series.type ?? model.type) === 'line',
      // Benchmark lines must not accumulate on the stacked sales axis.
      stack: model.stacked ? (series.benchmark ? `benchmark-${i}` : 'values') : undefined,
      borderWidth: series.points ? 1 : series.benchmark ? 3 : 2,
      borderDash: series.dash ?? (series.benchmark ? [8, 5] : []),
      pointStyle: series.pointStyle ?? (series.benchmark ? 'crossRot' as const : this.marker(teamIndex)),
      pointRadius: series.points ? series.points.map(point => point?.radius ?? 0) : 6,
      pointHitRadius: 12,
      pointHoverRadius: series.points ? series.points.map(point => (point?.radius ?? 0) + 2) : 8,
      spanGaps: false
    };
    if (!markers) return dataset;
    return {
      ...dataset,
      type: 'scatter' as const,
      // Legend contexts must retain the company's full-colour marker, even when its first point is historical.
      pointStyle: (context: {type: string; dataIndex?: number}) => context.type === 'data' && context.dataIndex != null
        ? markers[context.dataIndex] ?? this.marker(teamIndex) : this.marker(teamIndex)
    };
  }

  private options(model: DebriefChart): ChartOptions {
    return {
      responsive: true, maintainAspectRatio: false,
      animation: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? false : { duration: 220 },
      interaction: { mode: 'nearest', intersect: false },
      plugins: {
        legend: { display: true, labels: { usePointStyle: true, font: { size: 15 }, padding: 20,
          generateLabels: chart => {
            const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
            if (model.type !== 'scatter') return labels;
            return labels.map(label => {
              const series = model.series[label.datasetIndex!];
              const index = series.teamId ? Math.max(0, this.teamIds.indexOf(series.teamId)) : (series.styleIndex ?? label.datasetIndex!);
              const color = series.color ?? colors[index % colors.length];
              return {...label, pointStyle: this.marker(index, 10, color), fillStyle: color, strokeStyle: color};
            });
          }
        } },
        tooltip: { callbacks: {
            title: items => this.tooltipTitle(model, items),
            label: item => this.tooltipLabel(model, item)
          } }
      },
      scales: {
        x: { type: model.type === 'scatter' ? 'linear' : 'category', display: true, stacked: !!model.stacked, title: { display: true, text: model.xTitle, font: { size: 16 } }, ticks: { font: { size: 14 } } },
        y: { display: true, stacked: !!model.stacked, beginAtZero: model.type === 'bar', title: { display: true, text: model.yTitle, font: { size: 16 } }, ticks: { font: { size: 14 }, precision: model.integerTicks ? 0 : undefined, stepSize: model.integerTicks ? 1 : undefined } }
      }
    };
  }
  private tooltipTitle(model: DebriefChart, items: TooltipItem<DebriefChart["type"]>[]): string {
    const item = items[0];
    if (!item)
      return '';
    const point = model.series[item.datasetIndex].points?.[item.dataIndex];
    const title = point ? `Decision ${point.decision}` : model.decision ? `${item.label} · Decision ${model.decision}` : item.label;
    return model.companyName ? `${model.companyName} · ${title}` : title;
  }
  private tooltipLabel(model: DebriefChart, c: TooltipItem<DebriefChart["type"]>): string | string[] {
    const point = model.series[c.datasetIndex].points?.[c.dataIndex];
    const number = (n: number) => n.toLocaleString('en-GB', { maximumFractionDigits: 3 });
    return point ? [c.dataset.label ?? '', `Economic: ${number(point.y)} points`, `Environmental: ${number(point.environmental)} points`, `Social: ${number(point.social)} points`, `Environmental + Social: ${number(point.x)} points`]
      : `${c.dataset.label}: ${c.parsed.y == null ? 'N/A' : number(c.parsed.y)} ${model.yTitle}`;
  }
}
